import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, EyeOff } from "lucide-react";
import { getActivityImportance } from "@mspca-beti/shared";
import type { ActivityImportance } from "@mspca-beti/shared";

export interface ActivityRecord {
  id: string;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
  importance: ActivityImportance;
  taskId: string;
  taskTitle: string | null;
  user: {
    id: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
  } | null;
}

function getUserDisplay(user: ActivityRecord["user"]): string {
  if (!user) return "System";
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;
  return user.username;
}

function getUserInitials(user: ActivityRecord["user"]): string {
  if (!user) return "SY";
  if (user.firstName && user.lastName) return (user.firstName[0] + user.lastName[0]).toUpperCase();
  if (user.firstName) return user.firstName.slice(0, 2).toUpperCase();
  return user.username.slice(0, 2).toUpperCase();
}

export function formatActivityAction(record: ActivityRecord): string {
  const userName = getUserDisplay(record.user);
  const { action, field, oldValue, newValue } = record;

  switch (action) {
    case "task.created":
      return `${userName} created this task`;
    case "task.created_from_template":
      return newValue
        ? `${userName} created from template "${newValue}"`
        : `${userName} created this task from a template`;
    case "task.unarchived":
      return `${userName} restored this task`;
    case "task.updated": {
      switch (field) {
        case "title":
          return `${userName} changed the title`;
        case "description":
          return `${userName} updated the description`;
        case "due_date":
          return newValue
            ? `${userName} set due date to ${newValue}`
            : `${userName} removed the due date`;
        case "priority":
          return newValue
            ? `${userName} set priority to ${newValue}`
            : `${userName} removed the priority`;
        default:
          return `${userName} updated the task`;
      }
    }
    case "task.status_changed":
      return `${userName} moved from ${oldValue ?? "unknown"} to ${newValue ?? "unknown"}`;
    case "task.assigned":
      return newValue
        ? `${userName} assigned to ${newValue}`
        : `${userName} unassigned ${oldValue ?? "user"}`;
    case "task.archived":
      return `${userName} archived this task`;
    case "task.moved":
      return `${userName} moved this task to board "${newValue ?? "unknown"}"`;
    case "checklist_item.added":
      return `${userName} added checklist item "${newValue ?? ""}"`;
    case "checklist_item.checked":
      return `${userName} completed "${newValue ?? ""}"`;
    case "checklist_item.unchecked":
      return `${userName} unchecked "${newValue ?? ""}"`;
    case "checklist_item.deleted":
      return `${userName} removed checklist item "${oldValue ?? ""}"`;
    case "checklist_item.updated":
      return `${userName} renamed checklist item to "${newValue ?? ""}"`;
    case "comment.added":
      return `${userName} added a comment`;
    case "comment.replied":
      return `${userName} replied to a comment`;
    case "comment.edited":
      return `${userName} edited a comment`;
    case "comment.deleted":
      return `${userName} deleted a comment`;
    case "animal.linked":
      return `${userName} linked animal ${newValue ?? ""}`;
    case "animal.unlinked":
      return `${userName} unlinked animal ${oldValue ?? ""}`;
    case "interaction.linked":
      return `${userName} linked observation of ${newValue ?? "Unknown"}`;
    case "interaction.unlinked":
      return `${userName} unlinked observation of ${oldValue ?? "Unknown"}`;
    case "note.created":
      return `${userName} added a note`;
    case "note.updated":
      return `${userName} updated the note`;
    case "label.added":
      return `${userName} added label ${newValue ?? ""}`;
    case "label.removed":
      return `${userName} removed label ${oldValue ?? ""}`;
    default:
      return `${userName} performed ${action}`;
  }
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

interface ActivityFeedProps {
  activity: ActivityRecord[];
  isLoading?: boolean;
  showTaskTitle?: boolean;
  onTaskClick?: (taskId: string) => void;
  emptyMessage?: string;
  showAllDetails?: boolean;
  onToggleDetails?: (showAll: boolean) => void;
  maxItems?: number;
}

export function ActivityFeed({
  activity,
  isLoading = false,
  showTaskTitle = false,
  onTaskClick,
  emptyMessage = "No recent activity",
  showAllDetails,
  onToggleDetails,
  maxItems,
}: ActivityFeedProps) {
  const [localShowAll, setLocalShowAll] = useState(false);
  const isControlled = showAllDetails !== undefined;
  const showAll = isControlled ? showAllDetails : localShowAll;
  const toggleShowAll = () => {
    const next = !showAll;
    if (onToggleDetails) onToggleDetails(next);
    if (!isControlled) setLocalShowAll(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="task-activity-feed">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-start gap-3">
            <Skeleton className="h-7 w-7 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const importanceFiltered = isControlled
    ? activity
    : showAll
      ? activity
      : activity.filter(r => {
          const imp = getActivityImportance(r.action, r.field);
          return imp === "high" || imp === "medium";
        });

  const isCapped = !showAll && maxItems !== undefined && importanceFiltered.length > maxItems;
  const filtered = isCapped ? importanceFiltered.slice(0, maxItems) : importanceFiltered;
  const hiddenCount = isCapped ? importanceFiltered.length - maxItems : 0;

  const hasMoreItems = isControlled
    ? true
    : activity.length > filtered.length;

  return (
    <div data-testid="task-activity-feed">
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4" data-testid="task-activity-empty">
          {emptyMessage}
        </p>
      ) : (
        <div className="list-zebra">
          {filtered.map((record) => (
            <div
              key={record.id}
              className="flex items-start gap-3 px-3 py-2 rounded-md"
              data-testid={`task-activity-item-${record.id}`}
            >
              <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
                {record.user?.avatarUrl && (
                  <AvatarImage src={getAvatarSrc(record.user.avatarUrl, record.user.id)} alt={getUserDisplay(record.user)} />
                )}
                <AvatarFallback className="text-[10px]">
                  {getUserInitials(record.user)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground leading-snug">
                  {showTaskTitle && record.taskTitle && (
                    <>
                      {onTaskClick ? (
                        <button
                          className="font-medium hover:underline text-left"
                          onClick={() => onTaskClick(record.taskId)}
                          data-testid={`task-activity-link-${record.taskId}`}
                        >
                          {record.taskTitle}
                        </button>
                      ) : (
                        <span className="font-medium">{record.taskTitle}</span>
                      )}
                      <span className="text-muted-foreground"> — </span>
                    </>
                  )}
                  {formatActivityAction(record)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatRelativeTime(record.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isControlled && hasMoreItems && !showAll && filtered.length > 0 && (
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleShowAll}
            className="w-full text-muted-foreground"
            data-testid="task-activity-toggle-details"
          >
            <Eye className="h-3.5 w-3.5 mr-1" />
            View all activity{hiddenCount > 0 ? ` (${hiddenCount} more)` : ""}
          </Button>
        </div>
      )}
      {!isControlled && showAll && (
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleShowAll}
            className="w-full text-muted-foreground"
            data-testid="task-activity-toggle-details"
          >
            <EyeOff className="h-3.5 w-3.5 mr-1" />
            Show less
          </Button>
        </div>
      )}
    </div>
  );
}
